import React, { useState } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Empty,
  Tooltip,
  message,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '@/services/api.service';

const { Option } = Select;

const FORM_TYPES = [
  { key: 'BILANS_STANJA', label: 'Bilans stanja', color: 'blue' },
  { key: 'BILANS_USPEHA', label: 'Bilans uspeha', color: 'purple' },
];

const COMPANY_SIZE_COLORS = {
  MIKRO: 'cyan',
  MALO: 'orange',
  SREDNJE: 'gold',
  VELIKO: 'magenta',
};

export default function ValidationRulesPanel({
  validationRules,
  _loading,
  companySizes,
  validationSeverities,
  validationOperators,
  getLocalizedLabel,
}) {
  const { t } = useTranslation(['tables', 'forms', 'messages', 'menus']);
  const fs = (key) => t(`financialStatements.${key}`, { ns: 'tables' });
  const queryClient = useQueryClient();

  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleForm] = Form.useForm();

  // Create rule mutation
  const createRuleMutation = useMutation({
    mutationFn: (data) => API.financialStatements.createValidationRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-validation-rules']);
      setRuleModalVisible(false);
      ruleForm.resetFields();
      message.success(t('createSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Update rule mutation
  const updateRuleMutation = useMutation({
    mutationFn: ({ ruleId, data }) => API.financialStatements.updateValidationRule(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-validation-rules']);
      setRuleModalVisible(false);
      setSelectedRule(null);
      ruleForm.resetFields();
      message.success(t('updateSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Delete rule mutation
  const deleteRuleMutation = useMutation({
    mutationFn: (ruleId) => API.financialStatements.deleteValidationRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries(['financial-statements-validation-rules']);
      message.success(t('deleteSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  const handleOpenRuleModal = (rule = null) => {
    setSelectedRule(rule);
    if (rule) {
      ruleForm.setFieldsValue({
        name: rule.name,
        description: rule.description || '',
        companySizes: rule.companySizes,
        leftSideForm: rule.leftSide?.form,
        leftSideAopCode: rule.leftSide?.aopCode,
        operator: rule.operator,
        rightSideForm: rule.rightSide?.form,
        rightSideAopCode: rule.rightSide?.aopCode,
        severity: rule.severity,
        isActive: rule.isActive,
        order: rule.order || 0,
      });
    } else {
      ruleForm.resetFields();
      ruleForm.setFieldsValue({
        operator: 'EQUALS',
        severity: 'ERROR',
        isActive: true,
        order: 0,
      });
    }
    setRuleModalVisible(true);
  };

  const handleSaveRule = async () => {
    try {
      const values = await ruleForm.validateFields();

      const data = {
        name: values.name,
        description: values.description,
        companySizes: values.companySizes,
        leftSide: {
          form: values.leftSideForm,
          aopCode: values.leftSideAopCode,
        },
        operator: values.operator,
        rightSide: {
          form: values.rightSideForm,
          aopCode: values.rightSideAopCode,
        },
        severity: values.severity,
        isActive: values.isActive,
        order: values.order,
      };

      if (selectedRule) {
        updateRuleMutation.mutate({ ruleId: selectedRule._id, data });
      } else {
        createRuleMutation.mutate(data);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const getOperatorSymbol = (operatorKey) => {
    const operator = validationOperators?.find((op) => op.key === operatorKey);
    return operator?.symbol || operatorKey;
  };

  const columns = [
    {
      title: fs('ruleName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: fs('companySizes'),
      dataIndex: 'companySizes',
      key: 'companySizes',
      width: 180,
      render: (sizes) => (
        <Space wrap size={[4, 4]}>
          {sizes?.map((size) => (
            <Tag key={size} color={COMPANY_SIZE_COLORS[size] || 'default'}>
              {size}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: fs('leftSide'),
      key: 'leftSide',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tag color={FORM_TYPES.find((f) => f.key === record.leftSide?.form)?.color || 'default'}>
            {record.leftSide?.form === 'BILANS_STANJA' ? 'BS' : 'BU'}
          </Tag>
          <span className="font-mono">{record.leftSide?.aopCode}</span>
        </Space>
      ),
    },
    {
      title: fs('operator'),
      dataIndex: 'operator',
      key: 'operator',
      width: 80,
      align: 'center',
      render: (val) => (
        <span className="font-mono text-lg font-bold">{getOperatorSymbol(val)}</span>
      ),
    },
    {
      title: fs('rightSide'),
      key: 'rightSide',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tag color={FORM_TYPES.find((f) => f.key === record.rightSide?.form)?.color || 'default'}>
            {record.rightSide?.form === 'BILANS_STANJA' ? 'BS' : 'BU'}
          </Tag>
          <span className="font-mono">{record.rightSide?.aopCode}</span>
        </Space>
      ),
    },
    {
      title: fs('severity'),
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (val) => {
        const severity = validationSeverities?.find((s) => s.key === val);
        return (
          <Tag color={severity?.color || (val === 'ERROR' ? 'red' : 'orange')}>
            {getLocalizedLabel(severity || { labelEn: val })}
          </Tag>
        );
      },
    },
    {
      title: fs('isActive'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (val) => (
        <Tag color={val ? 'green' : 'default'}>
          {val ? fs('active') : fs('inactive')}
        </Tag>
      ),
    },
    {
      title: fs('order'),
      dataIndex: 'order',
      key: 'order',
      width: 70,
      align: 'center',
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 100,
      render: (_, rule) => (
        <Space>
          <Tooltip title={t('edit', { ns: 'menus' })}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenRuleModal(rule)}
            />
          </Tooltip>
          <Popconfirm
            title={fs('confirmDeleteValidationRule')}
            onConfirm={() => deleteRuleMutation.mutate(rule._id)}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rules = validationRules?.rules || validationRules || [];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          type="primary"
          className="btn-primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenRuleModal()}
        >
          {fs('createValidationRule')}
        </Button>
      </div>

      {rules.length === 0 ? (
        <Empty description={fs('noValidationRules')} />
      ) : (
        <Table
          dataSource={rules}
          columns={columns}
          rowKey="_id"
          size="middle"
          pagination={false}
          scroll={{ x: 1100 }}
        />
      )}

      {/* Rule Modal */}
      <Modal
        title={selectedRule ? fs('editValidationRule') : fs('createValidationRule')}
        open={ruleModalVisible}
        width={700}
        onCancel={() => {
          setRuleModalVisible(false);
          setSelectedRule(null);
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="default"
              className="btn-default btn-default--hover"
              onClick={() => {
                setRuleModalVisible(false);
                setSelectedRule(null);
              }}
            >
              {t('cancel', { ns: 'forms' })}
            </Button>
            <Button
              type="primary"
              className="btn-primary"
              loading={createRuleMutation.isPending || updateRuleMutation.isPending}
              onClick={handleSaveRule}
            >
              {t('save', { ns: 'forms' })}
            </Button>
          </div>
        }
      >
        <Form form={ruleForm} layout="vertical">
          <Form.Item
            name="name"
            label={fs('ruleName')}
            rules={[{ required: true, message: fs('ruleNameRequired') }]}
          >
            <Input placeholder={fs('ruleNamePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={fs('ruleDescription')}>
            <Input.TextArea rows={2} placeholder={fs('ruleDescriptionPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="companySizes"
            label={fs('companySizes')}
            rules={[{ required: true, message: fs('companySizesRequired') }]}
          >
            <Select
              mode="multiple"
              placeholder={fs('selectCompanySizes')}
              allowClear
            >
              {companySizes?.map((size) => (
                <Option key={size.key} value={size.key}>
                  {getLocalizedLabel(size)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div className="flex gap-4">
            <div className="flex-1 p-4 bg-blue-50 rounded-lg">
              <div className="text-sm font-medium mb-2 text-blue-700">{fs('leftSide')}</div>
              <Form.Item
                name="leftSideForm"
                label={fs('formType')}
                rules={[{ required: true, message: fs('formTypeRequired') }]}
              >
                <Select placeholder={fs('selectFormType')}>
                  {FORM_TYPES.map((type) => (
                    <Option key={type.key} value={type.key}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="leftSideAopCode"
                label={fs('aopCode')}
                rules={[{ required: true, message: fs('aopCodeRequired') }]}
              >
                <Input placeholder="e.g., 0410" className="font-mono" />
              </Form.Item>
            </div>

            <div className="flex items-center">
              <Form.Item
                name="operator"
                label={fs('operator')}
                rules={[{ required: true, message: fs('operatorRequired') }]}
                className="mb-0"
              >
                <Select style={{ width: 100 }}>
                  {validationOperators?.map((op) => (
                    <Option key={op.key} value={op.key}>
                      <span className="font-mono">{op.symbol}</span> {getLocalizedLabel(op)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div className="flex-1 p-4 bg-purple-50 rounded-lg">
              <div className="text-sm font-medium mb-2 text-purple-700">{fs('rightSide')}</div>
              <Form.Item
                name="rightSideForm"
                label={fs('formType')}
                rules={[{ required: true, message: fs('formTypeRequired') }]}
              >
                <Select placeholder={fs('selectFormType')}>
                  {FORM_TYPES.map((type) => (
                    <Option key={type.key} value={type.key}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="rightSideAopCode"
                label={fs('aopCode')}
                rules={[{ required: true, message: fs('aopCodeRequired') }]}
              >
                <Input placeholder="e.g., 1055" className="font-mono" />
              </Form.Item>
            </div>
          </div>

          <div className="flex gap-4 mt-4">
            <Form.Item
              name="severity"
              label={fs('severity')}
              rules={[{ required: true, message: fs('severityRequired') }]}
              className="flex-1"
            >
              <Select placeholder={fs('selectSeverity')}>
                {validationSeverities?.map((sev) => (
                  <Option key={sev.key} value={sev.key}>
                    <Tag color={sev.color}>{getLocalizedLabel(sev)}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="order" label={fs('order')} className="w-24">
              <InputNumber min={0} max={999} />
            </Form.Item>

            <Form.Item name="isActive" label={fs('isActive')} valuePropName="checked" className="w-24">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
